/**
 * Zod-backed numeric parsing for form inputs (locale decimals, empty strings, NaN, overflow).
 */

import { z } from 'zod'

function normalizeDecimalInput(raw: string): string {
  const s = String(raw ?? '')
    .trim()
    .replace(/\s/g, '')
  if (!s) return ''

  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')

  if (lastComma >= 0 && lastDot >= 0) {
    if (lastComma > lastDot) {
      return s.replace(/\./g, '').replace(',', '.')
    }
    return s.replace(/,/g, '')
  }

  if (lastComma >= 0 && lastDot < 0) {
    return s.replace(',', '.')
  }

  return s
}

/**
 * Optional dollar/major-unit amount from a text field → integer cents (USD or display units).
 */
export function parseOptionalMoneyStringToCents(value: string): number | null {
  const normalized = normalizeDecimalInput(value)
  if (!normalized) return null

  const schema = z.number().finite().nonnegative()
  const major = Number(normalized)
  const checked = schema.safeParse(major)
  if (!checked.success) return null

  const cents = Math.round(checked.data * 100)
  if (!Number.isSafeInteger(cents)) return null
  return cents
}

const batchSaleBase = z.object({
  saleType: z.enum(['percentage', 'fixed']),
  saleValue: z.number().finite().positive(),
  saleDays: z.number().int().min(1).max(365),
})

export const batchSaleFormSchema = batchSaleBase.superRefine((data, ctx) => {
  if (data.saleType === 'percentage') {
    if (data.saleValue < 1 || data.saleValue > 99 || !Number.isInteger(data.saleValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Percentage must be a whole number from 1 to 99',
        path: ['saleValue'],
      })
    }
  }
})

export type BatchSaleFormInput = z.infer<typeof batchSaleBase>
